import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { isApiError, type ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import type { SignInError } from '@/lib/auth'
import { PUBLIC_QUERY_SCOPE, useSession } from '@/lib/session'

/**
 * Acesso: o que as telas fora da casca pedem à API (docs/api/me.md e docs/api/acesso.md). Entrar,
 * sair e conferir o código passam pelo Supabase Auth, na sessão (`useSession`); aqui ficam a troca
 * da senha (`PUT /me/password`), o convite (`GET` e `POST /access/invite`), o pedido do código
 * (`POST /access/code`), o cadastro próprio (`POST /access/signup`) e as clínicas em que a pessoa
 * pode entrar (`GET /me/clinics`).
 */

export interface ChangePasswordInput {
  /** Só quando a tela pede a senha atual: quem entrou por código ou não tem senha não informa. */
  currentPassword?: string
  newPassword: string
}

/** RN-AUT-06: de 8 a 72 caracteres, com pelo menos uma letra e um número. */
export function isAcceptablePassword(password: string) {
  return (
    password.length >= 8 && password.length <= 72 && /\p{L}/u.test(password) && /\d/.test(password)
  )
}

/** O mesmo teste do login: algo, arroba, domínio com ponto. Quem valida de verdade é a API. */
export function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

/**
 * Troca a senha e renova a sessão: o token novo vem sem a marca de troca obrigatória, e o app
 * segue como no login (RT-04, RN-AUT-05). A chamada não é idempotente: a tela trava o botão.
 */
export function useChangePassword() {
  const api = useApi()
  const session = useSession()
  return useMutation({
    mutationFn: async (input: ChangePasswordInput) => {
      await api.put<{ mustChangePassword: boolean; mustRefreshSession: boolean }>(
        '/me/password',
        input,
      )
      await session.passwordChanged()
    },
  })
}

/** O convite, como `GET /access/invite` devolve: quem foi convidado e para qual clínica. */
export interface Invite {
  name: string
  email: string
  /** `null` para quem entra sem clínica (root). */
  clinicName: string | null
  expiresAt: string | null
}

/** Lê o convite pelo token do link (`/invite?t=…`). Vencido, usado ou adulterado: 410, uma frase só. */
export function useInvite(token: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: [PUBLIC_QUERY_SCOPE, 'invite', token],
    queryFn: () => api.get<Invite>('/access/invite', { t: token }),
    enabled: Boolean(token),
    // O convite não muda enquanto a tela está aberta, e reler depois do aceite devolveria 410.
    staleTime: Infinity,
    retry: false,
  })
}

export interface AcceptInviteInput {
  token: string
  password: string
}

/**
 * Cria a primeira senha pelo convite e entra com ela (RN-AUT-05). A senha fica gravada mesmo que a
 * entrada falhe em seguida: `signInError` diz por quê, e o convite já não vale para tentar de novo.
 */
export function useAcceptInvite() {
  const api = useApi()
  const session = useSession()
  return useMutation({
    mutationFn: async (input: AcceptInviteInput): Promise<{ signInError: SignInError | null }> => {
      const { email } = await api.post<{ email: string }>('/access/invite', input)
      const { error } = await session.signIn(email, input.password)
      return { signInError: error }
    },
  })
}

/** A resposta de `POST /access/code`: sempre a mesma, exista o e-mail ou não (RN-AUT-07). */
export interface CodeRequested {
  message: string
  expiresInMinutes: number
}

/** Pede o código de 6 dígitos de "Entrar com código". Quem confere é a sessão (`verifyCode`). */
export function useRequestCode() {
  const api = useApi()
  return useMutation({
    mutationFn: (email: string) => api.post<CodeRequested>('/access/code', { email }),
  })
}

/** O corpo de `POST /access/signup`: a pessoa e a clínica dela (RN-AUT-26). */
export interface SignupInput {
  name: string
  email: string
  password: string
  clinic: { name: string; docNumber: string }
}

/** A resposta: para qual e-mail o código foi e por quantos minutos vale. */
export interface SignupStarted {
  email: string
  expiresInMinutes: number
}

/**
 * Cria a conta, a clínica e o vínculo de gestora, e manda o código que confirma o e-mail. Quem
 * confere o código é a sessão (`verifyCode`): é ele que confirma o endereço e abre a primeira
 * sessão. Um 409 com `pendingVerification` diz que o cadastro já tinha começado e parou antes do
 * código: o caminho é "Entrar com código".
 */
export function useSignup() {
  const api = useApi()
  return useMutation({
    mutationFn: (input: SignupInput) => api.post<SignupStarted>('/access/signup', input),
  })
}

/** O 409 do cadastro parado antes do código (`pendingVerification`). */
export function isPendingVerification(error: unknown): boolean {
  return isApiError(error) && error.details.pendingVerification === true
}

/** Uma clínica em que a pessoa pode entrar, como `GET /me/clinics` devolve. */
export interface UserClinic {
  id: string
  name: string
  city: string
  state: string
  role: string
  isDefault: boolean
  /** `active`, `invited` ou `blocked` (o vínculo, ou a clínica bloqueada pela Westhetics). */
  status: string
  /** A clínica do token. */
  isCurrent: boolean
}

/**
 * As clínicas em que a pessoa pode entrar. Quem não é root recebe a lista inteira; root busca por
 * nome, cidade ou documento e recebe uma página por vez (docs/api/me.md).
 */
export function useUserClinics(params: { search?: string; page?: number } = {}) {
  const api = useApi()
  const search = params.search?.trim() ?? ''
  const page = params.page ?? 1
  return useQuery({
    queryKey: ['user-clinics', search, page],
    queryFn: () => api.list<UserClinic, ListMeta>('/me/clinics', { search, page }),
    // A lista anterior fica na tela enquanto a busca responde: sem piscar esqueleto a cada tecla.
    placeholderData: keepPreviousData,
  })
}

// Consentimento OAuth de um cliente MCP (docs/api/mcp.md, "Consentimento e conexões") --------------

/** O pedido de conexão como `GET /me/authorizations/:id` devolve enquanto falta consentir. */
export interface OAuthAuthorization {
  id: string
  oauthClient: { id: string; name: string; website: string | null; logo: string | null }
  /** Para onde o navegador volta depois da resposta: aparece por extenso na tela. */
  redirectUri: string
  scopes: string[]
  user: { name: string; email: string }
  isRoot: boolean
  /** A clínica em que a conexão opera (a padrão da pessoa); `null` para root, em visão global. */
  clinic: { id: string; name: string; role: string } | null
}

export type AuthorizationState =
  | { status: 'pending'; authorization: OAuthAuthorization }
  /** O mesmo aplicativo já foi autorizado antes: a tela volta a ele sem perguntar. */
  | { status: 'already_authorized'; redirectUrl: string }

/** O pedido é lido uma vez: ele vence em minutos e não muda enquanto a tela está aberta. */
export function useAuthorization(id: string | null, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: ['oauth-authorization', id],
    queryFn: () => api.get<AuthorizationState>(`/me/authorizations/${id}`),
    enabled: enabled && Boolean(id),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/** Aprova ou nega. A resposta traz o endereço para onde o navegador volta (RN-AUT-24). */
export function useDecideAuthorization(id: string) {
  const api = useApi()
  return useMutation({
    mutationFn: (decision: 'approve' | 'deny') =>
      api.post<{ redirectUrl: string }>(`/me/authorizations/${id}/${decision}`),
  })
}
